/**
 * VIP service — reads the operator-granted VIP entitlement for an account
 * (docs/features/admin-vip-unlimited-access, D8).
 *
 * This service does NOT manage state — that is `stores/billing.ts`'s job,
 * matching the existing billingService/store split.
 *
 * Read-only, front-end side: `vip_accounts` has no client write policy at
 * all (supabase/migrations/0038_vip_accounts.sql, D7) — the row is written
 * exclusively by `scripts/vip-access.mjs` with the service-role key.
 */
import { hasSupabase, getSupabase } from './supabaseService';
import type { VipStatus } from '@/types/billing';

/**
 * Fetches the VIP status for the given user.
 *
 * `maybeSingle()`, not `single()`: the common case is "no row" (not VIP),
 * and `maybeSingle()` resolves that as `data: null` with no error — unlike
 * `single()`, which would require the PGRST116-code handling
 * `billingService.fetchSubscription` needs for its own "no row" case.
 *
 * Fails CLOSED on any error — the deliberate inverse of `useQuotaGate`
 * (which fails open, `useQuotaGate.ts:56-59,:75-79`). A failed read here
 * must never paint an "Unlimited access" badge on an account that isn't
 * one; the real entitlement is decided server-side by `check_quota`
 * regardless of what this read returns, so failing closed costs a badge.
 *
 * A failure is also REPORTED (`failed: true`) so the store can tell "this
 * account has no row" from "we could not find out" and retry the latter
 * (review P2). The polarity does not change: a failure still yields
 * `isVip: false`, it just is not a memoisable answer.
 *
 * @param userId - The `auth.users.id` to check.
 * @returns VIP status; `isVip: false` on any error, missing row, or when
 *   Supabase isn't configured (dev without a backend — no gauge to lift).
 *   `failed: true` marks the two error paths, which are retryable.
 */
export const fetchVipStatus = async (userId: string): Promise<VipStatus> => {
  if (!hasSupabase()) return { isVip: false };

  try {
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from('vip_accounts')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('[vipService] fetchVipStatus error:', error.message);
      return { isVip: false, failed: true };
    }

    return { isVip: data !== null };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[vipService] Unexpected error:', msg);
    return { isVip: false, failed: true };
  }
};
